// components/CharacterInput.js - Improved version with better visibility
import React, { useState, useEffect, useRef } from 'react';
import ContactsIcon from "@mui/icons-material/Contacts";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { formatInputs } from "../utils/generalFunctions";
import classes from "../Assets/Styles/Transcriber.module.css";
import { GET_REMOVED_IMPORTED_CHARACTERS } from "../ApiEndpoints";

const CharacterInput = ({ projectId, existingCharacters, onAddNewCharacter, onSelectImportedCharacter }) => {
    const [inputValue, setInputValue] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [removedCharacters, setRemovedCharacters] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);

    // Fetch removed characters on component mount
    useEffect(() => {
        const fetchRemovedCharacters = async () => {
            if (!projectId) return;

            setLoading(true);

            try {
                const response = await axios.post(GET_REMOVED_IMPORTED_CHARACTERS, {
                    projectId
                });

                // Filter out characters that already exist in the current project
                const existingIds = existingCharacters.map(char => char.speaker);
                const filteredResults = response.data.removedCharacters.filter(
                    char => !existingIds.includes(char.speaker)
                );

                console.log("Removed characters found:", filteredResults.length);
                setRemovedCharacters(filteredResults);
            } catch (err) {
                console.error("Error fetching removed characters:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchRemovedCharacters();
    }, [projectId, existingCharacters]);

    // Filter suggestions based on input
    useEffect(() => {
        if (!inputValue.trim()) {
            setSuggestions([]);
            return;
        }

        const lowercasedInput = inputValue.toLowerCase();
        const filtered = removedCharacters.filter(char => {
            const displayName = (char.displayName || '').toLowerCase();
            // const actorName = (char.actorName || '').toLowerCase();
            // const speaker = (char.speaker || '').toLowerCase();
            return (
                displayName.includes(lowercasedInput) 
                // ||
                // actorName.includes(lowercasedInput) 
                // ||
                // speaker.includes(lowercasedInput)
            );
        });

        setSuggestions(filtered);
    }, [inputValue, removedCharacters]);

    // Handle clicking outside the component
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                inputRef.current &&
                !inputRef.current.contains(event.target) &&
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleInputChange = (e) => {
        const value = formatInputs(e.target.value);
        setInputValue(value);

        if (value.trim()) {
            setShowSuggestions(true);
            setHighlightedIndex(-1);
        } else {
            setShowSuggestions(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(prev =>
                prev < suggestions.length - 1 ? prev + 1 : prev
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();

            if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
                // Select the highlighted suggestion
                handleSelectSuggestion(suggestions[highlightedIndex]);
            } else if (inputValue.trim()) {
                // Add as new character
                handleAddNew();
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    const handleAddNew = () => {
        if (!inputValue.trim()) return;

        onAddNewCharacter(inputValue.trim());
        setInputValue("");
        setShowSuggestions(false);
    };

    const handleSelectSuggestion = (character) => {
        onSelectImportedCharacter(character);
        setInputValue("");
        setShowSuggestions(false);
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <textarea
                ref={inputRef}
                className={classes["AudioWorkSpeakerEditInput"]}
                value={inputValue}
                type="text"
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => inputValue.trim() && setShowSuggestions(true)}
                placeholder="Enter Character Name"
            />

            {showSuggestions && (
                <div
                    ref={suggestionsRef}
                    style={{
                        position: 'absolute',
                        zIndex: 9999,
                        width: '220px',
                        backgroundColor: '#ffffff',
                        border: '1px solid #d0d0d0',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        borderRadius: "var(--radius-md)",
                        marginTop: '2px',
                        maxHeight: '200px',
                        overflowY: 'auto'
                    }}
                >
                    {loading ? (
                        <div style={{ padding: '15px', textAlign: 'center' }}>
                            <CircularProgress size={24} />
                        </div>
                    ) : suggestions.length > 0 ? (
                        <div>
                            {suggestions.map((character, index) => (
                                <div
                                    key={`${character.speaker}-${index}`}
                                    onClick={() => handleSelectSuggestion(character)}
                                    style={{
                                        padding: '8px 10px',
                                        cursor: 'pointer',
                                        backgroundColor: highlightedIndex === index ? '#f0f7ff' : '#ffffff',
                                        borderBottom: '1px solid #eeeeee',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <div style={{ marginRight: '8px', color: '#5570dc' }}>
                                        <ContactsIcon style={{ fontSize: '1.2rem' }} />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 500, color: '#000000' }}>
                                            {character.displayName || character.speaker}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : inputValue.trim() ? (
                        <div style={{
                            padding: '10px',
                            textAlign: 'center',
                            fontSize: 'var(--font-size-normal)',
                            color: '#333333',
                            backgroundColor: '#f9f9f9'
                        }}>
                            No removed characters found. <br />
                            Press Enter to add as new.
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default CharacterInput;